import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Footer from "../../components/Footer/Footer";
import FloatingContact from "../../components/FloatingContact/FloatingContact";
import { getApiUrl, API_ENDPOINTS } from "../../config/apiConfig";
import "./BlogDetail.css";

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);

  useEffect(() => {
    fetchBlogDetail();
  }, [slug]);

  const fetchBlogDetail = async () => {
    try {
      setLoading(true);
      // TODO: Replace with your actual API endpoint
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.getBlogBySlug)}/${slug}`);

      if (response.ok) {
        const data = await response.json();
        // Assuming API returns { status: 200, data: blog }
        setBlog(data.data);
        // Fetch related blogs
        fetchRelatedBlogs(data.data.category, data.data.id);
      } else {
        // Show mock data if API is not ready
        setBlog(getMockBlog());
        setRelatedBlogs(getMockRelatedBlogs());
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
      setBlog(getMockBlog());
      setRelatedBlogs(getMockRelatedBlogs());
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedBlogs = async (category, currentBlogId) => {
    try {
      // TODO: Replace with your actual API endpoint
      const response = await fetch(
        `${getApiUrl(API_ENDPOINTS.getPublishedBlogs)}?category=${category}&exclude=${currentBlogId}&limit=3`
      );

      if (response.ok) {
        const data = await response.json();
        setRelatedBlogs(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching related blogs:', error);
    }
  };

  // Mock data for demonstration
  const getMockBlog = () => ({
    id: 1,
    title: "Understanding Urology: Common Myths Debunked",
    content: `
      <h2>Introduction</h2>
      <p>Urology is a medical specialty that deals with diseases of the urinary tract and male reproductive system. Despite its importance, there are many misconceptions about urological health that can prevent people from seeking proper care.</p>

      <h2>Common Myths About Urology</h2>

      <h3>Myth 1: Only Men Need Urologists</h3>
      <p>While urologists do treat male-specific conditions, they also treat urinary tract issues in both men and women. Women often see urologists for urinary incontinence, bladder infections, and other urinary system problems.</p>

      <h3>Myth 2: Prostate Problems Only Affect Older Men</h3>
      <p>Prostate issues can affect men of all ages. While prostate enlargement is more common in older men, younger men can experience prostatitis (inflammation) and other prostate-related conditions.</p>

      <h3>Myth 3: Blood in Urine Always Means Cancer</h3>
      <p>Hematuria (blood in urine) can be caused by many things, including urinary tract infections, kidney stones, or benign conditions. While it should always be evaluated by a doctor, it doesn't automatically indicate cancer.</p>

      <h2>Importance of Regular Check-ups</h2>
      <p>Regular urological check-ups are important for maintaining urinary and reproductive health. Early detection of problems can lead to more effective treatment and better outcomes.</p>

      <h2>Conclusion</h2>
      <p>Don't let myths prevent you from taking care of your urological health. If you have concerns about your urinary or reproductive health, consult with a qualified urologist for personalized advice and care.</p>
    `,
    excerpt: "Learn the truth about common misconceptions in urology and men's health. Discover evidence-based facts about prostate health, urinary issues, and modern treatment approaches.",
    category: "Health Tips",
    featuredImage: null,
    youtubeLink: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    createdAt: "2026-04-03T10:00:00.000Z",
    updatedAt: "2026-04-03T10:00:00.000Z",
    author: "Dr. Abhinav Singh",
    tags: ["urology", "myths", "health", "prostate"],
    isPublished: true,
    allowComments: true
  });

  const getMockRelatedBlogs = () => [
    {
      id: 2,
      title: "Prostate Health: What Every Man Should Know",
      excerpt: "A comprehensive guide to maintaining optimal prostate health.",
      category: "Medical Advice",
      slug: "prostate-health-what-every-man-should-know",
      createdAt: "2026-04-02T15:30:00.000Z"
    },
    {
      id: 3,
      title: "Advanced Treatment Options in Urology",
      excerpt: "Explore the latest treatment methods available today.",
      category: "Treatment",
      slug: "advanced-treatment-options-in-urology",
      createdAt: "2026-04-01T09:15:00.000Z"
    }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getYouTubeVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const renderYouTubeEmbed = (url) => {
    const videoId = getYouTubeVideoId(url);
    if (!videoId) return null;

    return (
      <div className="youtube-embed">
        <iframe
          width="100%"
          height="400"
          src={`https://www.youtube.com/embed/${videoId}`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="blog-detail-page">
        <main className="blog-detail-main">
          <div className="container">
            <div className="loading-blog">
              <div className="loading-header"></div>
              <div className="loading-content"></div>
              <div className="loading-content"></div>
              <div className="loading-content"></div>
            </div>
          </div>
        </main>
        <Footer />
        <FloatingContact />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="blog-detail-page">
        <main className="blog-detail-main">
          <div className="container">
            <div className="blog-not-found">
              <h1>Blog Not Found</h1>
              <p>The blog post you're looking for doesn't exist or has been removed.</p>
              <Link to="/blogs" className="back-to-blogs">← Back to Blogs</Link>
            </div>
          </div>
        </main>
        <Footer />
        <FloatingContact />
      </div>
    );
  }

  return (
    <div className="blog-detail-page">

      <main className="blog-detail-main">
        <div className="container">
          <article className="blog-article">
            {/* Blog Header */}
            <header className="blog-header">
              <div className="blog-meta">
                <span className="blog-category">{blog.category}</span>
                <span className="blog-date">📅 {formatDate(blog.createdAt)}</span>
                <span className="blog-author">👨‍⚕️ {blog.author}</span>
              </div>

              <h1 className="blog-title">{blog.title}</h1>

              <p className="blog-excerpt">{blog.excerpt}</p>

              {/* Tags */}
              {blog.tags && blog.tags.length > 0 && (
                <div className="blog-tags">
                  {blog.tags.map((tag, index) => (
                    <span key={index} className="tag">#{tag}</span>
                  ))}
                </div>
              )}
            </header>

            {/* Featured Image */}
            {blog.featuredImage && (
              <div className="blog-featured-image">
                <img src={blog.featuredImage} alt={blog.title} />
              </div>
            )}

            {/* YouTube Video */}
            {blog.youtubeLink && (
              <div className="blog-video-section">
                <h3>📹 Related Video</h3>
                {renderYouTubeEmbed(blog.youtubeLink)}
              </div>
            )}

            {/* Blog Content */}
            <div
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />

            {/* Share Section */}
            <div className="blog-share">
              <h3>Share this article</h3>
              <div className="share-buttons">
                <button className="share-btn facebook">📘 Facebook</button>
                <button className="share-btn twitter">🐦 Twitter</button>
                <button className="share-btn linkedin">💼 LinkedIn</button>
                <button className="share-btn whatsapp">💬 WhatsApp</button>
              </div>
            </div>
          </article>

          {/* Related Blogs */}
          {relatedBlogs.length > 0 && (
            <section className="related-blogs">
              <h2>Related Articles</h2>
              <div className="related-grid">
                {relatedBlogs.map((relatedBlog) => (
                  <article key={relatedBlog.id} className="related-card">
                    <h4>
                      <Link to={`/blog/${relatedBlog.slug}`}>{relatedBlog.title}</Link>
                    </h4>
                    <p>{relatedBlog.excerpt}</p>
                    <div className="related-meta">
                      <span className="related-category">{relatedBlog.category}</span>
                      <span className="related-date">{formatDate(relatedBlog.createdAt)}</span>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {/* Back to Blogs */}
          <div className="back-navigation">
            <Link to="/blogs" className="back-to-blogs">← Back to All Blogs</Link>
          </div>
        </div>
      </main>

      <Footer />
      <FloatingContact />
    </div>
  );
};

export default BlogDetail;